import { Button, Card, Form, message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  hideAuthLoader,
  hideMessage,
  setInitUrl,
  showAuthLoader,
  userSignInDataSuccess,
  userSignInSuccess,
} from "../appRedux/actions/Auth";
import notify from "../Notification";
import AuthService from "../service/AuthService";
import { getPostLoginRedirectUrl } from "../util/AuthRedirect";
import "./Auth.css";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 120;

// 125 -> "2:05"
const formatTime = (totalSeconds) => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const VerifyOTP = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { alertMessage, showMessage } = useSelector(({ auth }) => auth);

  // Data handed off by SignIn.js when the login response says isOtpRequired.
  const navState = location.state || {};
  const { tempToken: initialTempToken, userType, maskedEmail } = navState;

  const [tempToken, setTempToken] = useState(initialTempToken);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const inputsRef = useRef([]);
  const autoSubmittedRef = useRef(false);

  const resendStorageKey = initialTempToken
    ? `otp_resend_available_at_${initialTempToken}`
    : null;

  const readOrInitResendTarget = () => {
    if (!resendStorageKey) return Date.now();
    const stored = Number(localStorage.getItem(resendStorageKey));
    if (stored && stored > Date.now()) return stored;
    const target = Date.now() + RESEND_SECONDS * 1000;
    localStorage.setItem(resendStorageKey, String(target));
    return target;
  };

  const [resendAvailableAt, setResendAvailableAt] = useState(
    readOrInitResendTarget,
  );
  const [resendTimer, setResendTimer] = useState(() =>
    Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)),
  );

  // No tempToken means someone landed here directly (refresh / manual URL) -
  // send them back to sign in rather than showing a broken OTP form.
  useEffect(() => {
    if (!initialTempToken) {
      history.replace("/signin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((resendAvailableAt - Date.now()) / 1000),
      );
      setResendTimer(remaining);
    };
    tick();
    if (resendAvailableAt - Date.now() <= 0) return undefined;
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [resendAvailableAt]);

  const focusInput = (index) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  };

  const resetOtpBoxes = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    autoSubmittedRef.current = false;
    focusInput(0);
  };

  const handleChange = (index, rawValue) => {
    const digit = rawValue.replace(/[^0-9]/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // Clear current box first.
        setOtp((prev) => {
          const next = [...prev];
          next[index] = "";
          return next;
        });
      } else if (index > 0) {
        // Already empty: move to previous box and clear it too.
        focusInput(index - 1);
        setOtp((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setOtp(next);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const verifyOtp = async (code) => {
    setIsVerifying(true);
    dispatch(showAuthLoader());

    try {
      const res = await AuthService.verifyOtp({
        otp: code,
        tempToken,
        userType,
      });

      setIsVerifying(false);
      dispatch(hideAuthLoader());

      if (res.status === 200) {
        localStorage.setItem("user_token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data));
        notify.openNotificationWithIcon(
          "success",
          "Success",
          "Signin has been successfully done",
        );
        dispatch(userSignInSuccess(res));
        dispatch(userSignInDataSuccess(res.data));
        dispatch(hideMessage());
        dispatch(setInitUrl(""));
        if (resendStorageKey) {
          localStorage.removeItem(resendStorageKey);
        }

        const redirectUrl = getPostLoginRedirectUrl(res.data);
        history.replace(redirectUrl ? "/" + redirectUrl : "/erropage");
      } else {
        dispatch(hideMessage());
        notify.openNotificationWithIcon(
          "error",
          "Error",
          res.message || "Invalid OTP. Please try again.",
        );
        resetOtpBoxes();
      }
    } catch (error) {
      setIsVerifying(false);
      dispatch(hideAuthLoader());
      dispatch(hideMessage());
      notify.openNotificationWithIcon(
        "error",
        "Error",
        "Something went wrong while verifying OTP. Please try again.",
      );
      resetOtpBoxes();
    }
  };

  // Auto-submit the moment all 6 boxes are filled.
  useEffect(() => {
    const code = otp.join("");
    if (
      code.length === OTP_LENGTH &&
      !autoSubmittedRef.current &&
      !isVerifying
    ) {
      autoSubmittedRef.current = true;
      verifyOtp(code);
    }
    if (code.length < OTP_LENGTH) {
      autoSubmittedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleManualVerify = () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      notify.openNotificationWithIcon(
        "error",
        "Error",
        "Please enter the complete 6 digit OTP.",
      );
      return;
    }
    verifyOtp(code);
  };

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    try {
      setIsResending(true);
      const res = await AuthService.resendOtp({ tempToken, userType });
      setIsResending(false);

      if (res.status === 200) {
        if (res.data?.tempToken) {
          setTempToken(res.data.tempToken);
        }
        notify.openNotificationWithIcon(
          "success",
          "Success",
          "OTP has been resent to your registered email address.",
        );
        resetOtpBoxes();
        const newTarget = Date.now() + RESEND_SECONDS * 1000;
        if (resendStorageKey) {
          localStorage.setItem(resendStorageKey, String(newTarget));
        }
        setResendAvailableAt(newTarget);
      } else {
        notify.openNotificationWithIcon(
          "error",
          "Error",
          res.message || "Unable to resend OTP. Please try again.",
        );
      }
    } catch (error) {
      setIsResending(false);
      notify.openNotificationWithIcon(
        "error",
        "Error",
        "Unable to resend OTP. Please try again.",
      );
    }
  };

  return (
    <div
      className="gx-login-container"
      style={{
        backgroundColor: "rgb(4 74 143)",
        backgroundSize: "cover",
        position: "relative",
      }}
    >
      <div className="gx-login-content" style={{ maxWidth: 440 }}>
        <Form
          onFinish={handleManualVerify}
          className="gx-login-form gx-form-row0"
          layout="vertical"
        >
          <div className="gx-login-header gx-text-center">
            <h1 className="gx-login-title" style={{ textAlign: "center" }}>
              <span>2-Step Verification</span>
            </h1>
          </div>

          <Card
            style={{ marginBottom: 20 }}
            bordered={false}
            bodyStyle={{ padding: 0 }}
          >
            <p style={{ textAlign: "center", marginBottom: 0 }}>
              Enter the 6 digit code sent to{" "}
              <strong>{maskedEmail || "your registered email address"}</strong>
            </p>
          </Card>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginBottom: 12,
              opacity: isVerifying ? 0.5 : 1,
              transition: "opacity 0.15s ease",
            }}
          >
            {otp.map((digit, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                disabled={isVerifying}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                style={{
                  width: 46,
                  height: 54,
                  textAlign: "center",
                  fontSize: 22,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: "1px solid #d9d9d9",
                }}
              />
            ))}
          </div>
          <div
            className="gx-text-center"
            style={{
              minHeight: 26,
              marginBottom: 12,
              color: "#1890ff",
              fontWeight: 600,
            }}
          />
          <div className="gx-text-center">
            {resendTimer > 0 ? (
              <span>Resend OTP in {formatTime(resendTimer)}</span>
            ) : (
              <Button
                type="link"
                loading={isResending}
                disabled={isVerifying}
                onClick={handleResend}
                style={{ padding: 0 }}
              >
                Resend OTP
              </Button>
            )}
          </div>
        </Form>
      </div>
      {isVerifying && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.35)",
            pointerEvents: "none",
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
        </div>
      )}
      {showMessage ? message.error(alertMessage.toString()) : null}
    </div>
  );
};

export default VerifyOTP;
